import React, {
  useCallback,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import api from 'api';
import confirm from 'modules/confirm';
import classNames from 'classnames';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import InputField from 'components/InputField';
import { LEAD_STAGES, LeadStages } from 'models/Lead';
import { Button, Loader } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import ListingContext from '../../../../providers/Listing/Listing.context';
import { CheckboxGroupPlaceholders } from './checkboxplaceholders';
import { FileAccessSelector } from 'components/FileAccessSelector';
import {
  STEP_COMPLETION_ACTION,
  StepWorkflowConfiguration,
  WorkflowTemplateValues,
} from 'models/WorkflowConfiguration';
import useSpecificRules from '../../hooks/useSpecificRules';
import useGeneralRules from '../../hooks/useGeneralRules';
import { Link } from 'react-router-dom';
import DealContext from '../../../../providers/Deal';
import { NodeChangedPermission } from 'components/FileAccessSelector/types';
import { getHasElementsInArray, sortDataRoomNodes } from 'utils';
import AutomationProgresser from '../AutomationProgresser';
import arrayMutators from 'final-form-arrays';
import { ADD_NEW_ANSWER_INITIAL } from '../AutomationQuestion/AutomationQuestion.component';
import {
  extractQuestionUpdateValues,
  extractUpdateValues,
  getIsCompletionAction,
} from './utils';
import GuideCard from 'components/GuideCard';
import SparklesIcon from 'icons/Sparkles.icon';
import { FILES_SELECTOR, RULES_SELECTOR } from '../../data';
import WorkflowPopupNotificationContext from '../../../../providers/WorkflowPopupNotification/WorkflowPopupNotification.context';
import { isEqual } from 'lodash';
import { SUCCESS_MESSAGES } from 'request-responses';

import './WorkflowTemplate.styles.scss';
import './WorkflowTemplate.styles.responsive.scss';

type WorkflowTemplateProps = {
  className?: string;
  page: LeadStages;
};

const WorkflowTemplate: React.FC<WorkflowTemplateProps> = ({
  className,
  page,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAccessLoading, setIsAccessLoading] = useState(false);

  const [data, setData] = useState<StepWorkflowConfiguration>();

  const { workflowConfigurationStep, dataRoomNodes, checkedFilesIds } =
    data || {};

  const {
    immediatelyAcceptVerifiedBuyers,
    automaticallySendNDA,
    automaticallySendNDAEnabled,
    maxCapacity,
    maxCapacityEnabled,
    completionAction,
    progressionQuestion,
  } = workflowConfigurationStep || {};

  const { question, answers } = progressionQuestion || {};

  const rulesInitialValues = useMemo(
    () => ({
      maxCapacity,
      generalRules: { immediatelyAcceptVerifiedBuyers },
      specificRules: { automaticallySendNDA },
    }),
    [automaticallySendNDA, immediatelyAcceptVerifiedBuyers, maxCapacity],
  );

  const questionInitialValues = useMemo(
    () => ({
      completionAction: completionAction || STEP_COMPLETION_ACTION.KEEP,
      progressionQuestion: {
        question,
        answers: getHasElementsInArray(answers)
          ? answers.map(({ text, advancesLead }) => ({
              text,
              advancesLead,
            }))
          : [ADD_NEW_ANSWER_INITIAL],
      },
    }),
    [answers, completionAction, question],
  );

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const {
    canAccessDataRoom,
    listing: { envelopeTemplateName },
    workflowConfiguration: { _id: workflowId },
  } = useContext(ListingContext);
  const { leadStagesCopies } = useContext(DealContext);
  const { startCountdown, setField } = useContext(
    WorkflowPopupNotificationContext,
  );

  const classes = classNames(className, 'workflow-template');

  const { t } = useTranslation();

  const generalRulesOptions = useGeneralRules();
  const specificRulesOptions = useSpecificRules({
    automaticallySendNDA,
    automaticallySendNDAEnabled,
    envelopeTemplateName,
  });

  const hasSpecificRules = useMemo(
    () => getHasElementsInArray(specificRulesOptions),
    [specificRulesOptions],
  );

  const showSpecificRules = useMemo(
    () => isLoading || hasSpecificRules || page !== LEAD_STAGES.ENQUIRE,
    [hasSpecificRules, isLoading, page],
  );

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.portfolio.seller.getWorkflowConfiguration(
          workflowId,
          page,
        );

        setData(data);
      } catch (err) {
        popServerError(err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [page, workflowId, popServerError]);

  const onSubmitRules = useCallback(
    async (values: WorkflowTemplateValues, { getState }) => {
      try {
        const { modified } = getState();

        const updateValues = extractUpdateValues(values, modified);

        const { data } = await api.portfolio.seller.saveWorkflowConfiguration(
          workflowId,
          page,
          updateValues,
        );

        startCountdown();
        setData((prev) => ({ ...prev, ...data }));
      } catch (e) {
        popServerError(e);
      }
    },
    [page, popServerError, startCountdown, workflowId],
  );

  const onSubmitQuestions = useCallback(
    async (values: WorkflowTemplateValues) => {
      try {
        const updateValues = extractQuestionUpdateValues(values);

        const { data } = await api.portfolio.seller.saveWorkflowConfiguration(
          workflowId,
          page,
          updateValues,
        );

        setData((prev) => ({ ...prev, ...data }));

        if (
          getIsCompletionAction(updateValues.completionAction) &&
          !getHasElementsInArray(checkedFilesIds)
        ) {
          confirm({
            title: t('noFilesAdded'),
            content: t('noFilesAddedDescription'),
            submitBtnText: t('understood'),
            onlyOneOption: true,
          });
          return;
        }

        popSuccess(t(SUCCESS_MESSAGES.UPDATED_WORKFLOW_CONFIGURATION));
      } catch (e) {
        popServerError(e);
      }
    },
    [workflowId, page, checkedFilesIds, popSuccess, t, popServerError],
  );

  const updateFileAccess = useCallback(
    async ({ dataRoomNodeId, enable }: NodeChangedPermission) => {
      try {
        setIsAccessLoading(true);

        setField(dataRoomNodeId);

        const {
          data: { checkedFilesIds },
        } = await api.portfolio.seller.saveWorkflowConfiguration(
          workflowId,
          page,
          {
            dataRoomDocumentsAvailability: [
              { dataRoomNodeId, checked: enable },
            ],
          },
        );

        setData((prev) => ({ ...prev, checkedFilesIds }));
        startCountdown();
      } catch (e) {
        popServerError(e);
      } finally {
        setIsAccessLoading(false);
      }
    },
    [workflowId, page, setField, startCountdown, popServerError],
  );

  const getChildrenNodes = useCallback(
    async (id?: string) => {
      const {
        data: { dataRoomNodes, checkedFilesIds },
      } = await api.portfolio.seller.getWorkflowConfiguration(
        workflowId,
        page,
        id,
      );

      setData((prev) => ({ ...prev, checkedFilesIds }));

      return sortDataRoomNodes(dataRoomNodes);
    },
    [page, workflowId],
  );

  const getBaseNodes = useCallback(() => dataRoomNodes, [dataRoomNodes]);

  return (
    <div className={classes}>
      {isAccessLoading && <Loader />}

      <div className="workflow-template__form-content">
        <section className="workflow-template__rules-section">
          <Form
            onSubmit={onSubmitRules}
            initialValues={rulesInitialValues}
            render={({ submitting, handleSubmit }) => (
              <form
                onSubmit={handleSubmit}
                onChange={(e: React.ChangeEvent<HTMLFormElement>) => {
                  setField(e.target.name?.replaceAll('.', '-'));
                  handleSubmit();
                }}
              >
                <p className="wft-title">
                  {t('stageRules')}
                  <GuideCard
                    onHover
                    hasCloseButton={false}
                    anchor={
                      <span className="guide-anchor" id={RULES_SELECTOR}>
                        <SparklesIcon />
                      </span>
                    }
                    title="setStageRules"
                    description="setStageRulesDescription"
                  />
                </p>
                <p className="wft-title">{t('generalRules')}</p>
                {isLoading ? (
                  <CheckboxGroupPlaceholders />
                ) : (
                  <CheckboxFieldGroup
                    name="generalRules"
                    options={generalRulesOptions}
                  />
                )}

                {showSpecificRules && (
                  <>
                    <p className="wft-title">{t('specificRules')}</p>
                    <p>{t('setRulesThatApply')}</p>
                  </>
                )}

                {isLoading ? (
                  <CheckboxGroupPlaceholders />
                ) : hasSpecificRules ? (
                  <CheckboxFieldGroup
                    name="specificRules"
                    options={specificRulesOptions}
                  />
                ) : null}

                {page !== LEAD_STAGES.ENQUIRE ? (
                  <Field
                    name="maxCapacity"
                    id="maxCapacity"
                    component={InputField}
                    label={t('setMaxCapacity')}
                    disabled={!maxCapacityEnabled}
                  />
                ) : (
                  <div className="qualificiations-section">
                    <p>{t('qualification')}</p>
                    <Link to="../../qualification-questions">
                      {t('seeQualificationQuestions')}
                    </Link>
                  </div>
                )}
                {submitting && <Loader />}
              </form>
            )}
          />

          <Form
            onSubmit={onSubmitQuestions}
            initialValues={questionInitialValues}
            mutators={{ ...arrayMutators }}
            render={({ submitting, values, form, handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <AutomationProgresser page={page} isLoading={isLoading} />
                {!isEqual(questionInitialValues, values) && (
                  <div className="actions">
                    <p>{t('youHaveUnsavedChanges')}</p>
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => {
                        form.reset();
                        form.restart();
                      }}
                    >
                      {t('discard')}
                    </Button>
                    <Button type="submit" disabled={submitting}>
                      {t('save')}
                    </Button>
                  </div>
                )}
              </form>
            )}
          />
        </section>

        {canAccessDataRoom && (
          <section className="workflow-template__documents-section">
            <p className="wft-title">
              {t('filesInThisStage')}
              <GuideCard
                onHover
                hasCloseButton={false}
                anchor={
                  <span className="guide-anchor" id={FILES_SELECTOR}>
                    <SparklesIcon />
                  </span>
                }
                title="pickStageFiles"
                description="pickStageFilesDescription"
              />
            </p>
            <p className="wft-title">{t('files')}</p>
            <p>
              {t('thisDocumentsWillBeAccessible', {
                stage: leadStagesCopies[page],
              })}
            </p>

            {isLoading ? (
              <CheckboxGroupPlaceholders itemCount={4} />
            ) : (
              <FileAccessSelector
                checkedFilesIds={checkedFilesIds}
                getBaseNodes={getBaseNodes}
                updateFileAccess={updateFileAccess}
                getChildrenForNode={getChildrenNodes}
              />
            )}
          </section>
        )}
      </div>
    </div>
  );
};
export default WorkflowTemplate;
